<script lang="ts" setup>
  import { type Component, onMounted, ref } from 'vue'

  import { useTitrop } from '@/composables/useTitrop.ts'
  import { useGeneratedWorktimeTrackingDayStore } from '@/stores'
  import WidgetSkeleton from '@/components/dashboard/WidgetSkeleton.vue'
  import { apiWorktimetrackingentryproposalPost, WorktimeTrackingDayJsonldDefaultRead, WorktimeTrackingDayVersionEntryJsonldDefaultRead } from '@/api'
  import { DateTime } from 'luxon'
  import TabPanel from 'primevue/tabpanel'
  import DataTable, { type DataTableRowContextMenuEvent } from 'primevue/datatable'
  import { AddTimestampIcon, AutoSetIcon, BreakStartIcon, BreakStopIcon, EditTimeIcon, HistoryIcon, NoteIcon, OfficeIcon, WorkStopIcon } from '@/config/icons'
  import { useI18n } from 'vue-i18n'
  import ButtonDefault from '@/elements/ButtonDefault.vue'

  import Tabs from 'primevue/tabs'
  import TabList from 'primevue/tablist'
  import Tab from 'primevue/tab'
  import TabPanels from 'primevue/tabpanels'
  import WorktimeVersionsOverview from '@/components/worktimetracking/WorktimeVersionsOverview.vue'
  import EditWorktrackingDayForm from '@/components/worktimetracking/EditWorktrackingDayForm.vue'
  import AddWorktrackingDayForm from '@/components/worktimetracking/AddWorktrackingDayForm.vue'

  import { useEventBus } from '@vueuse/core'
  import ContextMenu from 'primevue/contextmenu'
  import { useToast } from 'primevue/usetoast'

  interface emptyDay {
    date: string
    versions: []
    activeVersion: undefined
    employee: string
  }

  const dayStore = useGeneratedWorktimeTrackingDayStore()

  const { t } = useI18n()
  const titrop = useTitrop()
  /*const router = useRouter()*/
  const toastService = useToast()

  type tableDataType = WorktimeTrackingDayJsonldDefaultRead | emptyDay

  const isLoading = ref<boolean>(true)
  const activeTab = ref(0)

  const relatedDays = ref<tableDataType[]>([])

  const isVersionsDialogVisible = ref<boolean>(false)
  const versionsToShow = ref<string[] | undefined>()
  const dateForVersionsOV = ref<string>()
  const activeVersForOV = ref<string>()

  const isAddWTTDayFormVisible = ref<boolean>(false)
  const dateToAdd = ref<string>()

  const isEditDayFormVisible = ref<boolean>(false)
  const dateToEdit = ref<string>()
  const dayToEdit = ref<WorktimeTrackingDayJsonldDefaultRead>()

  const contextMenu = ref()
  const selectedEntry = ref<WorktimeTrackingDayVersionEntryJsonldDefaultRead>()
  const menuModel = ref([
    { label: t('approve'), command: async () => approveDraftEntry(selectedEntry.value as WorktimeTrackingDayVersionEntryJsonldDefaultRead) },
    { label: t('decline'), command: () => declineDraftEntry(selectedEntry.value as WorktimeTrackingDayVersionEntryJsonldDefaultRead) }
  ])

  type EntryAction = 'BREAK_BEGIN' | 'BREAK_END' | 'WORK_BEGIN' | 'WORK_END'

  interface ActionConfig {
    label: string
    icon: Component
  }

  const actionConfig: Record<EntryAction, ActionConfig> = {
    BREAK_BEGIN: { label: t('breakStart'), icon: BreakStartIcon },
    BREAK_END: { label: t('breakEnd'), icon: BreakStopIcon },
    WORK_BEGIN: { label: t('workStart'), icon: OfficeIcon },
    WORK_END: { label: t('workEnd'), icon: WorkStopIcon }
  }

  onMounted(async () => {
    if (titrop.value.user.currentIri) await refreshData()
    isLoading.value = false
  })

  const refreshData = async () => {
    relatedDays.value = (
      await dayStore.findAll(
        {
          query: {
            employee: titrop.value.user.currentIri,
            'date[after]': DateTime.now().startOf('day').minus({ day: 2 }).toISO({ suppressMilliseconds: true }),
            'date[before]': DateTime.now().endOf('day').toISO({ suppressMilliseconds: true })
          }
        },
        { forceRefresh: true }
      )
    ).value
    if (relatedDays.value.length < 3) {
      for (let i = 0; i < 3; i++) {
        const temp = DateTime.now().startOf('day').minus({ day: i }).toISO({ suppressMilliseconds: true })
        if (!relatedDays.value.find((d) => d.date === temp))
          relatedDays.value.push({
            date: temp,
            versions: [],
            activeVersion: undefined,
            employee: titrop.value.user.currentIri
          })
      }
    }
    relatedDays.value.sort((a, b) => DateTime.fromISO(b.date as string).toMillis() - DateTime.fromISO(a.date as string).toMillis())
  }

  const isSystemMessage = (note: string) => {
    return note === 'Wurde automatisch generiert. Grund: Statusänderung.'
  }

  /*const openLink = (id: string) => {
    router.push({ name: 'working-time-corr-requests', query: { dayID: id } })
  }*/

  const onShowVersionsClick = (day: tableDataType) => {
    versionsToShow.value = day.versions
    dateForVersionsOV.value = day.date
    if (day.activeVersion) activeVersForOV.value = day.activeVersion['@id']
    else activeVersForOV.value = undefined
    isVersionsDialogVisible.value = true
  }

  const onAddDayClick = (day: tableDataType) => {
    dateToAdd.value = day.date
    isAddWTTDayFormVisible.value = true
  }

  const onEditDayClick = (day: tableDataType) => {
    dateToEdit.value = day.date
    dayToEdit.value = day as WorktimeTrackingDayJsonldDefaultRead
    isEditDayFormVisible.value = true
  }

  useEventBus('worktime_action_changed').on(async () => {
    await refreshData()
  })

  useEventBus('state_change_and_day_end_post').on(async () => {
    await refreshData()
  })

  const onRowContextMenu = (event: DataTableRowContextMenuEvent, dayIndex: number, day: tableDataType) => {
    if (event.data.draft && titrop.value.user.currentIri === day.employee) {
      selectedEntry.value = event.data
      contextMenu.value[dayIndex].show(event.originalEvent)
    } else selectedEntry.value = undefined
  }

  const approveDraftEntry = async (entry: WorktimeTrackingDayVersionEntryJsonldDefaultRead) => {
    await apiWorktimetrackingentryproposalPost({
      body: {
        accept: true,
        entryId: entry.id
      }
    })
    await refreshData()
    toastService.add({ severity: 'success', life: 2500, summary: 'Gespeichert' })
  }

  const declineDraftEntry = (entry: WorktimeTrackingDayVersionEntryJsonldDefaultRead) => {
    dayToEdit.value = relatedDays.value.find((d) => d.activeVersion?.entries?.includes(entry)) as WorktimeTrackingDayJsonldDefaultRead
    dateToEdit.value = dayToEdit.value.date
    isEditDayFormVisible.value = true
  }

  const emitDataChange = () => {
    useEventBus('WTT_correction_gadget_data_change').emit()
  }

  const doRefreshAndEmit = async () => {
    await refreshData()
    emitDataChange()
  }
</script>

<template>
  <div class="max-w-full @container max-h-80 overflow-y-auto">
    <WidgetSkeleton
      v-if="isLoading"
      variant="table" />
    <div v-else>
      <Tabs
        v-model:value="activeTab"
        scrollable>
        <TabList>
          <Tab
            v-for="(day, index) in relatedDays"
            :key="day['@id']"
            class="!py-2.5"
            :value="index">
            {{ $formatter.Date(day.date) }}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel
            v-for="(day, index) in relatedDays"
            :key="day['@id']"
            :header="$formatter.Date(day.date)"
            :value="index">
            <ContextMenu
              ref="contextMenu"
              :model="menuModel"
              @hide="selectedEntry = undefined" />
            <DataTable
              v-model:context-menu-selection="selectedEntry"
              :value="day.activeVersion ? day.activeVersion.entries : []"
              size="small"
              class="text-sm"
              @row-contextmenu="onRowContextMenu($event, index, day)">
              <template #empty>
                <h5 class="w-full text-center">{{ t('noData') }}</h5>
              </template>
              <template #header>
                <div class="flex h-full w-full items-center justify-between">
                  <span class="h-full font-bold text-800">{{ t('timeline') }}</span>
                  <div class="flex h-full items-center">
                    <ButtonDefault
                      v-if="!day.activeVersion && !day.versions?.length"
                      v-tooltip="{ value: t('addDay'), modifiers: { top: true } }"
                      :icon="AddTimestampIcon"
                      outlined
                      size="small"
                      @click="onAddDayClick(day)" />
<!--                    <DecisionPendingIcon
                      v-if="day.decisionPending"
                      class="size-7 cursor-pointer hover:text-primary-300"
                      @click="openLink(day['@id'])" />-->
                    <ButtonDefault
                      v-if="day.versions?.length"
                      v-tooltip="{ value: t('editDay'), modifiers: { top: true } }"
                      :icon="EditTimeIcon"
                      class="ml-2"
                      outlined
                      size="small"
                      @click="onEditDayClick(day)" />
                    <ButtonDefault
                      v-if="day.versions?.length"
                      v-tooltip="{ value: t('versions'), modifiers: { top: true } }"
                      :icon="HistoryIcon"
                      class="ml-2"
                      outlined
                      size="small"
                      @click="onShowVersionsClick(day)" />
                  </div>
                </div>
              </template>
              <Column
                :field="(entry) => $formatter.TimeOnly(entry.timestamp)"
                header="Zeitpunkt" />
              <Column
                field="action"
                header="Aktion">
                <template #body="data">
                  <div class="flex items-center gap-2">
                    <component
                      :is="actionConfig[data.data.action as EntryAction].icon"
                      class="size-4.5 shrink-0 text-[var(--p-text-muted-color)]"
                      aria-hidden="true" />
                    {{ actionConfig[data.data.action as EntryAction].label }}
                    <span
                      v-if="data.data.note"
                      class="ml-2 flex">
                      <AutoSetIcon v-if="isSystemMessage(data.data.note)" />
                      <NoteIcon v-else />
                    </span>
                    <AutoSetIcon
                      v-if="data.data.draft"
                      class="ml-2" />
                  </div>
                </template>
              </Column>
            </DataTable>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <WorktimeVersionsOverview
        v-if="isVersionsDialogVisible"
        v-model:is-visible="isVersionsDialogVisible"
        :active-version="activeVersForOV"
        :date="dateForVersionsOV!"
        :related-employee="$titrop.user.currentIri"
        :versions-array="versionsToShow!" />

      <AddWorktrackingDayForm
        v-if="isAddWTTDayFormVisible"
        v-model:is-visible="isAddWTTDayFormVisible"
        :date="dateToAdd!"
        :employee="$titrop.user.currentIri"
        @entries-post="doRefreshAndEmit" />

      <EditWorktrackingDayForm
        v-if="isEditDayFormVisible"
        v-model:is-visible="isEditDayFormVisible"
        :date="dateToEdit!"
        :day-to-edit="dayToEdit!"
        @submit="doRefreshAndEmit" />
    </div>
  </div>
</template>

<i18n>
{
  "de": {
    "approve": "Genehmigen",
    "decline": "Ablehnen",
    "breakStart": "Pausenbeginn",
    "breakEnd": "Pausenende",
    "workStart": "Arbeitsbeginn",
    "workEnd": "Arbeitsende",
    "breakStarted": "Pause begonnen",
    "breakEnded": "Pause beendet",
    "workStarted": "Arbeit begonnen",
    "workEnded": "Arbeit beendet",
    "noData": "Keine Daten gefunden",
    "timeline": "Tagesverlauf",
    "noEntries": "Noch keine Zeiten erfasst",
    "addDay": "Tag hinzufügen",
    "editDay": "Tag bearbeiten",
    "versions": "Versionsübersicht",
    "viewRequest": "Anfrage öffnen",
    "workingTime": "Arbeitszeit:",
    "breakTime": "Pause:",
    "autoGenerated": "Wurde automatisch generiert. Grund: fehlender Eintrag."
  },
  "en": {
    "approve": "Approve",
    "decline": "Decline",
    "breakStart": "Break start",
    "breakEnd": "Break end",
    "workStart": "Work start",
    "workEnd": "Work end",
    "breakStarted": "Break started",
    "breakEnded": "Break ended",
    "workStarted": "Work started",
    "workEnded": "Work ended",
    "noData": "No data found",
    "timeline": "Daily timeline",
    "noEntries": "No times recorded yet",
    "addDay": "Add day",
    "editDay": "Edit day",
    "versions": "Version history",
    "viewRequest": "Open request",
    "workingTime": "Working time:",
    "breakTime": "Break:",
    "autoGenerated": "Generated automatically because an entry is missing."
  }
}
</i18n>
